import { KMDeckShareData, EncryptedShareData, SensitiveData, ReducedEncryptedShareData } from '../types/KMDeck';

/**
 * Encryption utilities for password-protected KMDeck sharing
 * Uses Web Crypto API with AES-256-GCM for secure encryption
 */

/**
 * Generates a random salt for key derivation
 */
function generateSalt(): Uint8Array {
  return crypto.getRandomValues(new Uint8Array(16));
}

/**
 * Generates a random IV for encryption
 */
function generateIV(): Uint8Array {
  return crypto.getRandomValues(new Uint8Array(12));
}

/**
 * Derives a cryptographic key from a password using PBKDF2
 */
async function deriveKey(password: string, salt: Uint8Array): Promise<CryptoKey> {
  const encoder = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    encoder.encode(password),
    'PBKDF2',
    false,
    ['deriveBits', 'deriveKey']
  );

  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: salt,
      iterations: 100000, // OWASP recommended minimum
      hash: 'SHA-256'
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

/**
 * Converts Uint8Array to base64 string
 */
function uint8ArrayToBase64(uint8Array: Uint8Array): string {
  return btoa(String.fromCharCode(...uint8Array));
}

/**
 * Converts base64 string to Uint8Array
 */
function base64ToUint8Array(base64: string): Uint8Array {
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}

/**
 * Encrypts KMDeck share data with a password
 */
export async function encryptShareData(
  shareData: KMDeckShareData, 
  password: string
): Promise<EncryptedShareData> {
  try {
    // Generate random salt and IV
    const salt = generateSalt();
    const iv = generateIV();
    
    // Derive key from password
    const key = await deriveKey(password, salt);
    
    // Convert share data to JSON string
    const dataString = JSON.stringify(shareData);
    const encoder = new TextEncoder();
    const dataBytes = encoder.encode(dataString);
    
    // Encrypt the data
    const encryptedBytes = await crypto.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv: iv
      },
      key,
      dataBytes
    );
    
    // Convert to base64 for URL encoding
    const encryptedData = uint8ArrayToBase64(new Uint8Array(encryptedBytes));
    const saltBase64 = uint8ArrayToBase64(salt);
    const ivBase64 = uint8ArrayToBase64(iv);
    
    return {
      encryptedData,
      salt: saltBase64,
      iv: ivBase64,
      isPasswordProtected: true
    };
  } catch (error) {
    console.error('[Encryption] Failed to encrypt share data:', error);
    throw new Error('Failed to encrypt data');
  }
}

/**
 * Decrypts KMDeck share data with a password
 */
export async function decryptShareData(
  encryptedData: EncryptedShareData,
  password: string
): Promise<KMDeckShareData> {
  try {
    // Convert base64 strings back to Uint8Arrays
    const salt = base64ToUint8Array(encryptedData.salt);
    const iv = base64ToUint8Array(encryptedData.iv);
    const encryptedBytes = base64ToUint8Array(encryptedData.encryptedData);
    
    // Derive key from password
    const key = await deriveKey(password, salt);
    
    // Decrypt the data
    const decryptedBytes = await crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: iv
      },
      key,
      encryptedBytes
    );
    
    // Convert back to string and parse JSON
    const decoder = new TextDecoder();
    const dataString = decoder.decode(decryptedBytes);
    const shareData = JSON.parse(dataString) as KMDeckShareData;
    
    return shareData;
  } catch (error) {
    console.error('[Encryption] Failed to decrypt share data:', error);
    throw new Error('Invalid password or corrupted data');
  }
}

/**
 * Validates password strength
 */
export function validatePasswordStrength(password: string): {
  isValid: boolean;
  score: number;
  feedback: string[];
} {
  const feedback: string[] = [];
  let score = 0;
  
  if (password.length < 8) {
    feedback.push('Password must be at least 8 characters long');
  } else {
    score += 1;
  }
  
  if (password.length >= 12) {
    score += 1;
  }
  
  if (!/[a-z]/.test(password)) {
    feedback.push('Password should contain lowercase letters');
  } else {
    score += 1;
  }
  
  if (!/[A-Z]/.test(password)) {
    feedback.push('Password should contain uppercase letters');
  } else {
    score += 1;
  }
  
  if (!/[0-9]/.test(password)) {
    feedback.push('Password should contain numbers');
  } else {
    score += 1;
  }
  
  if (!/[^a-zA-Z0-9]/.test(password)) {
    feedback.push('Password should contain special characters');
  } else {
    score += 1;
  }
  
  const isValid = score >= 4 && password.length >= 8;
  
  return {
    isValid,
    score,
    feedback
  };
}

/**
 * Generates a password strength indicator color
 */
export function getPasswordStrengthColor(score: number): string {
  if (score < 2) return '#e53e3e'; // Red
  if (score < 4) return '#dd6b20'; // Orange
  if (score < 6) return '#d69e2e'; // Yellow
  return '#38a169'; // Green
}

/**
 * Generates a password strength label
 */
export function getPasswordStrengthLabel(score: number): string {
  if (score < 2) return 'Very Weak';
  if (score < 4) return 'Weak';
  if (score < 6) return 'Good';
  return 'Strong';
}

/**
 * Encrypts only the sensitive data (remoteTriggerUrl and password hash)
 * This dramatically reduces the encrypted payload size
 */
export async function encryptSensitiveData(
  sensitiveData: SensitiveData, 
  password: string
): Promise<ReducedEncryptedShareData> {
  try {
    // Generate random salt and IV
    const salt = generateSalt();
    const iv = generateIV();
    
    // Derive key from password
    const key = await deriveKey(password, salt);
    
    // Convert sensitive data to JSON string
    const dataString = JSON.stringify(sensitiveData);
    const encoder = new TextEncoder();
    const dataBytes = encoder.encode(dataString);
    
    // Encrypt the data
    const encryptedBytes = await crypto.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv: iv
      },
      key,
      dataBytes
    );
    
    // Convert to base64 for URL encoding
    const encryptedData = uint8ArrayToBase64(new Uint8Array(encryptedBytes));
    const saltBase64 = uint8ArrayToBase64(salt);
    const ivBase64 = uint8ArrayToBase64(iv);
    
    return {
      encryptedSensitiveData: encryptedData,
      salt: saltBase64,
      iv: ivBase64,
      isPasswordProtected: true
    };
  } catch (error) {
    console.error('[Encryption] Failed to encrypt sensitive data:', error);
    throw new Error('Failed to encrypt sensitive data');
  }
}

/**
 * Decrypts only the sensitive data
 */
export async function decryptSensitiveData(
  encryptedData: ReducedEncryptedShareData,
  password: string
): Promise<SensitiveData> {
  try {
    // Convert base64 strings back to Uint8Arrays
    const salt = base64ToUint8Array(encryptedData.salt);
    const iv = base64ToUint8Array(encryptedData.iv);
    const encryptedBytes = base64ToUint8Array(encryptedData.encryptedSensitiveData);
    
    // Derive key from password
    const key = await deriveKey(password, salt);
    
    // Decrypt the data
    const decryptedBytes = await crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: iv
      },
      key,
      encryptedBytes
    );
    
    // Convert back to string and parse JSON
    const decoder = new TextDecoder();
    const dataString = decoder.decode(decryptedBytes);
    const sensitiveData = JSON.parse(dataString) as SensitiveData;
    
    return sensitiveData;
  } catch (error) {
    console.error('[Encryption] Failed to decrypt sensitive data:', error);
    throw new Error('Invalid password or corrupted data');
  }
}

/**
 * Generates a password hash for verification
 */
export async function generatePasswordHash(password: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(password);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}
